import React from 'react';
import { motion } from 'framer-motion';
import { Scale, Users, AlertTriangle, Heart } from 'lucide-react';
import './IsraelSlide.css';

// Import images
import smotrichImg from '../images/images for slides/Bezalel Smotrich.jpg';
import benGvirImg from '../images/images for slides/itamar Ben-Gvir.jpg';
import ighImg from '../images/images for slides/Gerichtshof Den Haag 1 aspect ratio.jpg';
import israelProtestImg from '../images/images for slides/israel-polizei-demonstranten-100.webp';

const IsraelFlag = () => (
  <svg width="48" height="36" viewBox="0 0 220 160" style={{ display: 'inline-block', verticalAlign: 'middle', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
    <rect width="220" height="160" fill="white"/>
    <rect y="25" width="220" height="25" fill="#005EB8"/>
    <rect y="110" width="220" height="25" fill="#005EB8"/>
    <g transform="translate(110, 80)" stroke="#005EB8" strokeWidth="9" fill="none">
      <polygon points="0,-36 31,18 -31,18" />
      <polygon points="0,36 -31,-18 31,-18" />
    </g>
  </svg>
);

function IsraelSlide({ showNotes }) {
  const ministers = [
    {
      name: 'Itamar Ben-Gvir',
      role: 'Sicherheitsminister',
      background: 'Früher verurteilt wegen Unterstützung einer Terrororganisation',
      color: '#ff3a3a',
      image: benGvirImg
    },
    {
      name: 'Bezalel Smotrich',
      role: 'Finanzminister',
      background: 'Nannte sich selbst einen "faschistischen Homophoben"',
      color: '#ff6b35',
      image: smotrichImg
    }
  ];

  const ideology = [
    { icon: '✡️', text: 'Religiöser Nationalismus und Siedlungsausbau' },
    { icon: '💬', text: 'Rhetorik der Entmenschlichung' },
  ];

  return (
    <div className="slide israel-slide">
      <div className="israel-layout">
        {/* Header */}
        <div className="israel-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            An der Macht (2)
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <IsraelFlag />
            </div>
            <span>Israel</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="slide-subtitle"
          >
            Religiöser Nationalismus an der Macht
          </motion.p>
        </div>

        <div className="israel-content">
          {/* Left Column */}
          <div className="israel-left">
            {/* Government Box */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="government-box"
            >
              <h3>Die Regierung <span className="since">(seit Ende 2022)</span></h3>
              <p className="gov-desc">Die Koalition der Radikalisierung</p>
              
              <div className="ministers-grid">
                {ministers.map((minister, index) => (
                  <motion.div
                    key={index}
                    className="minister-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    style={{ '--minister-color': minister.color }}
                  >
                    <div className="minister-image">
                      {minister.image ? (
                        <img src={minister.image} alt={minister.name} className="minister-photo-img" />
                      ) : (
                        <div className="minister-photo-placeholder">
                          <span>{minister.name.split(' ')[0]}</span>
                        </div>
                      )}
                    </div>
                    <div className="minister-info">
                      <h4>{minister.name}</h4>
                      <span className="minister-role">{minister.role}</span>
                      <p className="minister-bg">{minister.background}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Ideology Box */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="ideology-box"
            >
              <h3>Ideologie</h3>
              <div className="ideology-list">
                {ideology.map((item, index) => (
                  <div key={index} className="ideology-item">
                    <span className="ideology-icon">{item.icon}</span>
                    <span className="ideology-text">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="quote-highlight">
                <span className="quote-source">Verteidigungsminister Gallant:</span>
                <p>"menschliche Tiere"</p>
                <span className="quote-context">über Gegner</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="israel-right">
            {/* Gaza Box */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="gaza-box"
            >
              <div className="gaza-header">
                <Scale size={20} />
                <h3>Der Gaza-Krieg</h3>
              </div>
              
              <div className="gaza-content-wrapper">
                <div className="gaza-facts">
                  <div className="fact-item">
                    <span className="fact-icon">💔</span>
                    <span>Hohe zivile Opferzahlen, darunter viele Frauen und Kinder</span>
                  </div>
                  <div className="fact-item">
                    <span className="fact-icon">⚖️</span>
                    <span>Der IGH prüft derzeit Vorwürfe möglicher Völkerrechtsverletzungen</span>
                  </div>
                  <div className="fact-item pending">
                    <span className="fact-icon">⚠️</span>
                    <span>Der IGH warnt vor unmittelbarer Genozid-Gefahr</span>
                  </div>
                </div>

                {/* Image placeholder */}
                <div className="gaza-image">
                  <img src={ighImg} alt="Internationaler Gerichtshof Den Haag" className="gaza-photo" />
                </div>
              </div>
            </motion.div>

            {/* Important Note */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="important-box"
            >
              <div className="important-header">
                <Heart size={20} className="heart-icon" />
                <span>Wichtig: Regierung ≠ Bevölkerung</span>
              </div>
              
              <div className="important-content">
                <p>
                  <strong>2023 und 2024:</strong> Massenproteste in Israel – 
                  Hunderttausende demonstrierten gegen die eigene Regierung.
                </p>
                <p className="highlight-text">
                  Es ist kein Konflikt "Die Welt gegen Israel", sondern ein 
                  <strong> Kampf um die Demokratie innerhalb Israels</strong>.
                </p>
              </div>

              {/* Protest Image */}
              <div className="protest-image">
                <img src={israelProtestImg} alt="Proteste in Tel Aviv gegen die Regierung" className="protest-photo" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Regierung der Extreme:</strong> Netanjahus Koalitionspartner (Ben-Gvir, Smotrich) vertreten einen religiösen Suprematismus ("Das Land gehört nur uns"). 
          Diplomatie wird als Schwäche gesehen.<br/><br/>
          <strong>Folgen der Entmenschlichung:</strong> Begriffe wie "menschliche Tiere" (Gallant) bereiten den Boden für Gewalt. 
          Gleichzeitig werden Zeugen ausgeschaltet (fast 300 getötete Journalisten).<br/><br/>
          <strong>Der innere Kampf:</strong> Es ist kein Konflikt "Welt gegen Israel", sondern "Demokratie gegen Extremismus" innerhalb Israels. 
          Hunderttausende Israelis demonstrieren gegen den Umbau ihres Staates zur Diktatur.
        </div>
      )}
    </div>
  );
}

export default IsraelSlide;
